/**
 * Property details page
 * PURPOSE: Displays galleries, property information, contact actions, analytics and reviews.
 *
 * EDITING GUIDE:
 * - Read the comments above each section or function before changing behavior.
 * - Keep validation, authentication and error handling in place for production safety.
 * - Search for "EDIT HERE" to find the most common business-content settings.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { api, assetUrl } from '../api';
import { getVisitorId, trackEvent, trackEventBeacon } from '../analytics';
import LanguageSwitcher from '../components/LanguageSwitcher';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { hasValidPropertyCoordinates, PropertyLocationMap } from '../components/PropertyMap';
import { buildPropertyWhatsAppUrl } from '../utils/whatsapp';
import { useLanguage } from '../i18n';
import './PropertyDetails.css';

/**
 * Render a five-star display that can be read-only or clickable.
 */
/**
 * Format one custom-field value for the active public language.
 */
function formatCustomFieldValue(field, language, t) {
  if (field.fieldType === 'boolean') return field.value === 'true' ? t('yes') : t('no');
  if (field.fieldType === 'date') {
    const date = new Date(`${field.value}T00:00:00`);
    if (!Number.isNaN(date.getTime())) {
      const locale = language === 'ar' ? 'ar-TN' : language === 'en' ? 'en-GB' : 'fr-FR';
      return date.toLocaleDateString(locale);
    }
  }
  if (field.fieldType === 'number') {
    const number = Number(field.value);
    if (Number.isFinite(number)) {
      const locale = language === 'ar' ? 'ar-TN' : language === 'en' ? 'en-GB' : 'fr-FR';
      return number.toLocaleString(locale);
    }
  }
  return field.value;
}

function Stars({ value, onChange, interactive = false }) {
  return (
    <div className={`stars ${interactive ? 'interactive' : ''}`} aria-label={`${value} / 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          className={star <= value ? 'filled' : ''}
          onClick={() => interactive && onChange?.(star)}
          aria-label={`${star} stars`}
        >★</button>
      ))}
    </div>
  );
}

/**
 * Load and render one property, its gallery, analytics timing and customer reviews.
 */
const PropertyDetails = () => {
  // ROUTE AND TRANSLATION CONTEXT.
  const { id } = useParams();
  const { t, language } = useLanguage();

  // COMPONENT STATE: property, gallery, review form and loading feedback.
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average: 0, count: 0 });
  const [reviewForm, setReviewForm] = useState({ rating: 0, name: '', comment: '' });
  const [reviewMessage, setReviewMessage] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  // MUTABLE ANALYTICS/GALLERY REFERENCES: values persist without forcing a re-render.
  const allImagesRef = useRef([]);
  const visibleStartedAtRef = useRef(Date.now());
  const totalVisibleMsRef = useRef(0);
  const timeSentRef = useRef(false);

  /**
   * Capture visible time for analytics or interface state.
   */
  const captureVisibleTime = useCallback(() => {
    if (!visibleStartedAtRef.current) return;
    totalVisibleMsRef.current += Date.now() - visibleStartedAtRef.current;
    visibleStartedAtRef.current = null;
  }, []);

  /**
   * Send time spent to the backend.
   */
  const sendTimeSpent = useCallback(() => {
    if (timeSentRef.current) return;
    captureVisibleTime();
    timeSentRef.current = true;
    const seconds = Math.max(1, Math.round(totalVisibleMsRef.current / 1000));
    trackEventBeacon('property_time', { propertyId: id, durationSeconds: seconds });
  }, [captureVisibleTime, id]);

  // INITIAL LOAD AND TIME TRACKING: fetch data, record the open and watch page visibility.
  useEffect(() => {
    AOS.init({ duration: 700, once: true });
    window.scrollTo(0, 0);
    visibleStartedAtRef.current = document.visibilityState === 'visible' ? Date.now() : null;
    totalVisibleMsRef.current = 0;
    timeSentRef.current = false;

    Promise.all([
      api.get(`/api/properties/${id}`),
      api.get(`/api/reviews/property/${id}`).catch(() => ({ data: { data: [], stats: { average: 0, count: 0 } } })),
    ])
      .then(([propertyResponse, reviewResponse]) => {
        const loadedProperty = propertyResponse.data.data;
        setProperty(loadedProperty);
        setReviews(reviewResponse.data.data || []);
        setReviewStats(reviewResponse.data.stats || loadedProperty.reviewStats || { average: 0, count: 0 });
        trackEvent('property_open', { propertyId: id });
      })
      .catch((error) => console.error('Property details error:', error))
      .finally(() => setLoading(false));

    /**
     * Handle the visibility user action and keep interface state synchronized.
     */
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        captureVisibleTime();
      } else if (!timeSentRef.current && !visibleStartedAtRef.current) {
        visibleStartedAtRef.current = Date.now();
      }
    };
    window.addEventListener('pagehide', sendTimeSpent);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      sendTimeSpent();
      window.removeEventListener('pagehide', sendTimeSpent);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [captureVisibleTime, id, sendTimeSpent]);

  /**
   * Handle the key user action and keep interface state synchronized.
   */
  const handleKey = useCallback((event) => {
    if (!lightbox) return;
    if (event.key === 'ArrowRight') setLightboxIndex((index) => index === allImagesRef.current.length - 1 ? 0 : index + 1);
    if (event.key === 'ArrowLeft') setLightboxIndex((index) => index === 0 ? allImagesRef.current.length - 1 : index - 1);
    if (event.key === 'Escape') setLightbox(false);
  }, [lightbox]);

  // LIGHTBOX KEYBOARD CONTROLS: arrows navigate and Escape closes the overlay.
  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  /**
   * Validate and store a one-to-five-star review for an approved property.
   */
  const submitReview = async (event) => {
    event.preventDefault();
    setReviewMessage('');
    if (!reviewForm.rating) {
      setReviewMessage('Choisissez une note de 1 à 5 étoiles.');
      return;
    }
    setReviewLoading(true);
    try {
      const response = await api.post(`/api/reviews/property/${id}`, {
        ...reviewForm,
        visitorId: getVisitorId(),
      });
      setReviewStats(response.data.stats);
      const refreshed = await api.get(`/api/reviews/property/${id}`);
      setReviews(refreshed.data.data || []);
      setReviewForm({ rating: 0, name: '', comment: '' });
      // The backend confirms that the review is waiting for admin approval.
      setReviewMessage(t('reviewPending'));
    } catch (error) {
      setReviewMessage(error.response?.data?.message || 'Impossible d’enregistrer votre avis.');
    } finally {
      setReviewLoading(false);
    }
  };

  if (loading) return <div className="loader-box"><div className="custom-spinner" /></div>;
  if (!property) return <div className="error-container"><h2>Bien introuvable</h2><Link to="/">{t('back')}</Link></div>;

  // GALLERY FALLBACK: use the legacy single image or placeholder when necessary.
  const allImages = (property.images?.length ? property.images : [property.image]).filter(Boolean);
  if (!allImages.length) allImages.push('/placeholder.svg');
  allImagesRef.current = allImages;

  /**
   * Encapsulate the next slide behavior used by this file.
   */
  const nextSlide = () => setCurrentIndex((index) => index === allImages.length - 1 ? 0 : index + 1);
  /**
   * Encapsulate the prev slide behavior used by this file.
   */
  const prevSlide = () => setCurrentIndex((index) => index === 0 ? allImages.length - 1 : index - 1);
  const contactPhone = String(property.contactPhone || '21695999600').replace(/[^0-9]/g, '');
  // MAP LINK: use the same saved coordinates for the embedded map and external OpenStreetMap link.
  const hasMapLocation = hasValidPropertyCoordinates(property.latitude, property.longitude);
  const openStreetMapUrl = hasMapLocation
    ? `https://www.openstreetmap.org/?mlat=${property.latitude}&mlon=${property.longitude}#map=17/${property.latitude}/${property.longitude}`
    : '';

  return (
    <div className="details-master-container">
      <div className="top-nav-simple">
        <Link to="/" className="back-link-modern"><span>←</span> {t('back')}</Link>
        <LanguageSwitcher />
      </div>

      <div className="main-content-card" data-aos="fade-up">
        <div className="visual-side">
          {property.status !== 'disponible' && <div className={`status-badge ${property.status}`}>{property.status}</div>}
          <div className="property-badge">{property.type}</div>
          <div className="slider-wrapper">
            <img src={assetUrl(allImages[currentIndex])} alt={property.title} className="hero-img clickable-img" onClick={() => { setLightboxIndex(currentIndex); setLightbox(true); }} />
            <div className="zoom-hint">🔍 {t('zoom')}</div>
            {allImages.length > 1 && <>
              <button type="button" className="nav-arrow prev" onClick={prevSlide}>❮</button>
              <button type="button" className="nav-arrow next" onClick={nextSlide}>❯</button>
              <div className="slide-number">{currentIndex + 1} / {allImages.length}</div>
            </>}
          </div>
          {allImages.length > 1 && <div className="thumb-strip">{allImages.map((image, index) => <img key={`${image}-${index}`} src={assetUrl(image)} alt={`vue ${index + 1}`} className={`thumb ${index === currentIndex ? 'active' : ''}`} onClick={() => setCurrentIndex(index)} />)}</div>}
        </div>

        <div className="text-side">
          <div className="details-kicker">{property.category || 'Immobilier'} · {property.type}</div>
          <h1 className="main-title">{property.title}</h1>
          <p className="main-location">📍 {property.location}</p>
          <div className="details-rating-summary"><Stars value={Math.round(reviewStats.average || 0)} /><strong>{Number(reviewStats.average || 0).toFixed(1)}</strong><span>({reviewStats.count || 0})</span></div>

          <div className="price-box-premium"><span className="label">{t('propertyPrice')}</span><div className="value">{Number(property.price).toLocaleString()} <span className="currency">DT</span></div></div>

          <div className="details-spec-grid">
            {Number(property.area) > 0 && <div><span>↔</span><strong>{property.area} m²</strong></div>}
            {Number(property.bedrooms) > 0 && <div><span>🛏</span><strong>{property.bedrooms} {t('bedrooms')}</strong></div>}
            {Number(property.bathrooms) > 0 && <div><span>🚿</span><strong>{property.bathrooms} {t('bathrooms')}</strong></div>}
          </div>

          {/* CUSTOM FIELDS: administrator-created property facts are shown only when saved with this listing. */}
          {Array.isArray(property.customFields) && property.customFields.length > 0 && (
            <section className="property-custom-details">
              <h3>{t('additionalDetails')}</h3>
              <dl>
                {property.customFields.map((field, index) => (
                  <div key={`${field.label}-${index}`}>
                    <dt>{field.label}</dt>
                    <dd>{formatCustomFieldValue(field, language, t)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {property.description && <div className="description-content"><h3>{t('description')}</h3><div className="description-lines">{property.description.split('\n').map((line, index) => line.trim() ? <p key={index} className="desc-line">{line}</p> : <div key={index} className="desc-spacer" />)}</div></div>}

          <div className="button-group-premium">
            {/* The WhatsApp message includes this property's direct public URL for easy sharing. */}
            <a href={buildPropertyWhatsAppUrl(contactPhone, property)} className="action-btn-p whatsapp-style" target="_blank" rel="noreferrer">
              <WhatsAppIcon size={22} className="whatsapp-icon" />
              WhatsApp
            </a>
            <a href={`tel:+${contactPhone}`} className="action-btn-p phone-style"><span>📞</span> {t('call')}</a>
          </div>
        </div>
      </div>

      {/* PROPERTY MAP: shown only when the listing contains coordinates selected in a posting form. */}
      {hasMapLocation && (
        <section className="property-map-section" data-aos="fade-up">
          <div className="property-map-section-heading">
            <div>
              <span>{t('mapKicker')}</span>
              <h2>{t('mapTitle')}</h2>
              <p>{t('mapDescription')}</p>
            </div>
            <a href={openStreetMapUrl} target="_blank" rel="noreferrer">{t('openMap')} ↗</a>
          </div>
          <PropertyLocationMap
            latitude={property.latitude}
            longitude={property.longitude}
            title={property.title}
            location={property.location}
          />
        </section>
      )}

      {/* EDIT HERE: five-star property review form and public review list. */}
      <section className="reviews-section" data-aos="fade-up">
        <div className="reviews-heading"><div><span>5 ★</span><h2>{t('reviewsTitle')}</h2></div><div className="review-big-score"><strong>{Number(reviewStats.average || 0).toFixed(1)}</strong><Stars value={Math.round(reviewStats.average || 0)} /><small>{reviewStats.count || 0} avis</small></div></div>
        <div className="reviews-layout">
          <form className="review-form" onSubmit={submitReview}>
            <label><span>{t('yourRating')}</span><Stars value={reviewForm.rating} interactive onChange={(rating) => setReviewForm({ ...reviewForm, rating })} /></label>
            <label><span>{t('yourName')}</span><input maxLength="80" value={reviewForm.name} onChange={(event) => setReviewForm({ ...reviewForm, name: event.target.value })} /></label>
            <label><span>{t('yourComment')}</span><textarea maxLength="1000" value={reviewForm.comment} onChange={(event) => setReviewForm({ ...reviewForm, comment: event.target.value })} /></label>
            {reviewMessage && <p className="review-message">{reviewMessage}</p>}
            <button type="submit" disabled={reviewLoading}>{reviewLoading ? '...' : t('submitReview')}</button>
          </form>
          <div className="review-list">
            {reviews.length ? reviews.map((review) => <article key={review._id}><div><strong>{review.name || 'Visiteur'}</strong><Stars value={review.rating} /></div>{review.comment && <p>{review.comment}</p>}<time>{new Date(review.createdAt).toLocaleDateString()}</time></article>) : <div className="empty-reviews">{t('noReviews')}</div>}
          </div>
        </div>
      </section>

      {/* FULLSCREEN GALLERY: opens over the page and supports thumbnail navigation. */}
      {lightbox && <div className="lightbox-overlay" onClick={() => setLightbox(false)}>
        <button type="button" className="lb-close" onClick={() => setLightbox(false)}>✕</button>
        <div className="lb-counter">{lightboxIndex + 1} / {allImages.length}</div>
        <img src={assetUrl(allImages[lightboxIndex])} alt="fullscreen" className="lb-image" onClick={(event) => event.stopPropagation()} />
        {allImages.length > 1 && <><button type="button" className="lb-arrow lb-prev" onClick={(event) => { event.stopPropagation(); setLightboxIndex((index) => index === 0 ? allImages.length - 1 : index - 1); }}>❮</button><button type="button" className="lb-arrow lb-next" onClick={(event) => { event.stopPropagation(); setLightboxIndex((index) => index === allImages.length - 1 ? 0 : index + 1); }}>❯</button></>}
        {allImages.length > 1 && <div className="lb-thumbs" onClick={(event) => event.stopPropagation()}>{allImages.map((image, index) => <img key={`${image}-${index}`} src={assetUrl(image)} alt={`thumb ${index + 1}`} className={`lb-thumb ${index === lightboxIndex ? 'active' : ''}`} onClick={() => setLightboxIndex(index)} />)}</div>}
      </div>}
    </div>
  );
};

export default PropertyDetails;
